import { isDefined } from '@sgme/fp';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { isAlgoOrderType } from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import { type BlotterOrderEntry, orderIsEditable } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { ordersSlice } from '@/store/orders/slice.ts';

export const orderCommands = ['view', 'edit', 'cancel', 'pauseOrResume', 'fill'] as const;

export type OrderCommand = (typeof orderCommands)[number];

export type OrderCommandButtonState =
  | { present: true; enabled: true }
  | { present: true; enabled: false; reason: string }
  | { present: false };

export const ABSENT: OrderCommandButtonState = { present: false };
export const ENABLED: OrderCommandButtonState = { present: true, enabled: true };

export type ButtonsState = {
  [commmand in OrderCommand]: OrderCommandButtonState;
};

export function useButtonsState(entry: BlotterOrderEntry | undefined, algoCommandIsInProgress: boolean): ButtonsState {
  const { formatMessage } = useIntl();

  const userContext = useUserContext();
  const canTrade = checkIfUserCanTrade(userContext);
  const hasPermissionToTradeForClient =
    isDefined(entry?.clientId) && userContext.hasPermissionToTradeFor(entry.clientId);

  const orderTileExists = useAppSelector((state) => ordersSlice.selectors.orderTileExists(state, entry?.id));

  return useMemo(() => {
    const enabledIfCanTrade: OrderCommandButtonState = canTrade
      ? ENABLED
      : { present: true, enabled: false, reason: formatMessage({ id: 'blotter.tab.Order.command.notAllowed' }) };

    const states: ButtonsState = orderIsEditable(entry)
      ? isAlgoOrderType(entry?.orderType)
        ? {
            view: ENABLED,
            edit: enabledIfCanTrade,
            cancel: enabledIfCanTrade,
            pauseOrResume: enabledIfCanTrade,
            fill: enabledIfCanTrade,
          }
        : { view: ENABLED, edit: enabledIfCanTrade, cancel: enabledIfCanTrade, pauseOrResume: ABSENT, fill: ABSENT }
      : { view: ENABLED, edit: ABSENT, cancel: ABSENT, pauseOrResume: ABSENT, fill: ABSENT };

    if (!hasPermissionToTradeForClient) {
      return disableCommands(states, formatMessage({ id: 'blotter.tab.Order.command.disallowedClient' }));
    }

    if (orderTileExists) {
      // we won't create another tile for the same order, disabling all buttons that would create a tile
      // maybe TODO : allow the command, but automatically select the right workspace tab, ensure tile is visible (via scrolling) and flash it
      return disableCommands(states, formatMessage({ id: 'blotter.tab.Order.command.alreadySummoned' }));
    }

    if (algoCommandIsInProgress) {
      return disableCommands(states, formatMessage({ id: 'blotter.tab.Order.command.algoCommandInProgress' }), [
        'pauseOrResume',
        'fill',
      ]);
    }

    return states;
  }, [algoCommandIsInProgress, entry, canTrade, orderTileExists, hasPermissionToTradeForClient, formatMessage]);
}

const disableCommands = (
  buttonState: ButtonsState,
  reason: string,
  commands: readonly OrderCommand[] = orderCommands,
): ButtonsState =>
  Object.fromEntries(
    Object.entries(buttonState).map(([command, state]) =>
      commands.includes(command as OrderCommand) && state.present && state.enabled
        ? [
            command,
            {
              present: true,
              enabled: false,
              reason,
            },
          ]
        : [command, state],
    ),
  ) as ButtonsState;
